import { cn } from "@/lib/utils";
import { MapCanvas } from "@/components/common/MapCanvas";
import { Radar } from "lucide-react";

// Purely decorative backdrop for the logged-out auth screens — nobody has a
// session yet to fetch real fleet data with, so this is a small hand-placed
// scatter of real Dallas-area coordinates rather than live vehicles.
const DECORATIVE_VEHICLES = [
  { id: "auth-1", status: "moving", heading: 40, location: { lat: 32.7767, lng: -96.797 } },
  { id: "auth-2", status: "moving", heading: 210, location: { lat: 32.79, lng: -96.77 } },
  { id: "auth-3", status: "parked", location: { lat: 32.76, lng: -96.81 } },
  { id: "auth-4", status: "idle", location: { lat: 32.81, lng: -96.75 } },
  { id: "auth-5", status: "moving", heading: 120, location: { lat: 32.74, lng: -96.79 } },
  { id: "auth-6", status: "offline", location: { lat: 32.83, lng: -96.82 } },
];
const DECORATIVE_GEOFENCES = [
  { id: "auth-g1", type: "circle", status: "active", centerLat: 32.79, centerLng: -96.77, radius: 500 },
  { id: "auth-g2", type: "circle", status: "active", centerLat: 32.76, centerLng: -96.81, radius: 400 },
];
const DECORATIVE_FACILITIES = [{ id: "auth-f1", name: "Main Facility", latitude: 32.7767, longitude: -96.797 }];

// Split-screen auth shell: form on the left, live command-center preview on the right.
export function AuthShell({ children, testId }) {
  return (
    <div className="flex min-h-screen bg-background" data-testid={testId}>
      <div className="flex w-full flex-col justify-center px-6 py-10 sm:px-12 lg:w-[46%] lg:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-10 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/30">
              <Radar className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg font-semibold tracking-tight">ARC Beacon</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Kamar Technology</div>
            </div>
          </div>
          {children}
        </div>
      </div>

      <div className="relative hidden overflow-hidden lg:block lg:w-[54%]">
        {/* Explicit dark variant — the gradient fade below is designed against
            the moody dark basemap, not photographic satellite imagery, and this
            decorative backdrop isn't one of the operational map views the
            app-wide satellite default is meant for. */}
        <MapCanvas vehicles={DECORATIVE_VEHICLES} geofences={DECORATIVE_GEOFENCES} facilities={DECORATIVE_FACILITIES} controls={false} variant="dark" className="!rounded-none !border-0 h-full" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/20 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <p className="font-display text-3xl font-semibold leading-tight text-balance">
            The operating system for connected fleet visibility.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Monitor every vehicle, trip and alert across your fleet — in real time, from one command center.
          </p>
          <div className="mt-6 flex items-center gap-6 text-sm">
            <div><span className="font-display text-2xl font-semibold">128</span><span className="ml-2 text-muted-foreground">vehicles</span></div>
            <div><span className="font-display text-2xl font-semibold">47</span><span className="ml-2 text-muted-foreground">moving now</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, className, children }) {
  return (
    <label className={cn("block space-y-1.5", className)}>
      <span className="text-sm font-medium text-foreground/90">{label}</span>
      {children}
    </label>
  );
}
